import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { AlertTriangle, CheckCircle2, Loader2, Users, Brain } from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface CustomerRetentionChurnProps {
  startDate: string;
  endDate: string;
}

const getRiskVariant = (score: number): 'secondary' | 'outline' | 'destructive' => {
  if (score > 80) return 'destructive';
  if (score > 60) return 'outline';
  return 'secondary';
};

const CustomerRetentionChurn: React.FC<CustomerRetentionChurnProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      setData({
        retention_rate: 87.5,
        churn_rate: 12.5,
        at_risk_customers: 24,
        total_customers: 320,
        churn_trend: Array.from({ length: 6 }, (_, i) => ({
          month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i],
          churn: 10 + Math.random() * 8,
          retention: 85 + Math.random() * 10
        })),
        at_risk_list: [
          { name: 'Acme Corp', score: 85, factors: ['Low engagement', 'No recent activity', 'CSAT declining'], last_interaction: 45 },
          { name: 'Tech Solutions', score: 72, factors: ['Multiple escalations', 'Long resolution times'], last_interaction: 30 },
          { name: 'Global Inc', score: 68, factors: ['Negative sentiment'], last_interaction: 25 }
        ]
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertDescription>No retention data available.</AlertDescription>
      </Alert>
    );
  }

  const trendChartOptions: ApexOptions = {
    chart: { type: 'line', toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { categories: data.churn_trend.map((d: any) => d.month) },
    yaxis: { min: 0, max: 100, labels: { formatter: (val) => `${val.toFixed(0)}%` } },
    colors: ['#4caf50', '#f44336'],
    legend: { position: 'top' }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Retention Rate</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.retention_rate}%
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Active customer retention</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Churn Rate</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.churn_rate}%
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Customers lost this period</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>At-Risk Customers</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.at_risk_customers}
            </CardTitle>
          </CardHeader>
          <CardFooter className="gap-2">
            <Badge variant="destructive" className="text-xs">High Priority</Badge>
            <span className="text-xs text-muted-foreground">require attention</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Total Customers</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.total_customers.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Active accounts</span>
          </CardFooter>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Retention & Churn Trend (6 Months)</CardTitle>
          <CardDescription>Monthly retention vs churn rate comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <Chart
            options={trendChartOptions}
            series={[
              { name: 'Retention Rate', data: data.churn_trend.map((d: any) => d.retention) },
              { name: 'Churn Rate', data: data.churn_trend.map((d: any) => d.churn) }
            ]}
            type="line"
            height={300}
          />
        </CardContent>
      </Card>

      {/* At-Risk Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">At-Risk Customers (Churn Prediction)</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="text-center">Churn Risk Score</TableHead>
                <TableHead>Contributing Factors</TableHead>
                <TableHead className="text-right">Last Interaction</TableHead>
                <TableHead className="text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.at_risk_list.map((customer: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>
                    <span className="text-sm font-medium">{customer.name}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="space-y-1">
                      <Badge variant={getRiskVariant(customer.score)}>{customer.score}%</Badge>
                      <Progress value={customer.score} className="h-1.5 w-20 mx-auto" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {customer.factors.map((factor: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-xs">{factor}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs text-muted-foreground">{customer.last_interaction} days ago</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Button variant="destructive" size="sm">Contact Now</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Churn Prevention */}
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium mb-2">Churn Prevention Actions</p>
          <ul className="list-disc pl-4 space-y-1">
            <li><span className="text-xs">Immediately reach out to {data.at_risk_list.length} high-risk customers</span></li>
            <li><span className="text-xs">Review and resolve outstanding issues for at-risk accounts</span></li>
            <li><span className="text-xs">Offer loyalty programs or special support for declining engagement customers</span></li>
            <li><span className="text-xs">Schedule quarterly business reviews for high-value accounts</span></li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Churn Prediction Model */}
      <Card className="bg-muted/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Churn Prediction Model</CardTitle>
          </div>
          <CardDescription>Our AI model analyzes 10+ factors to predict churn risk</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground">
                <strong>Engagement Metrics:</strong> Login frequency, feature usage, support interactions
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">
                <strong>Satisfaction Indicators:</strong> CSAT scores, sentiment analysis, NPS
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">
                <strong>Behavioral Patterns:</strong> Response delays, escalations, complaint frequency
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerRetentionChurn;
